<template>
  <b-container
    v-if="currentTabIndex === 1"
    class="mt-2"
    fluid
  >
    <b-row>

      <b-col
        cols="12"
        md="8"
        class="px-1"
        order="2"
        order-md="1"
      >
        <b-form-group
          label="Search"
          label-for="filter_business_partner_search"
          label-class="font-weight-bolder"
        >
          <b-input
            id="filter_business_partner_search"
            v-model="tableBusinessPartner.filter.search"
            type="text"
            debounce="1000"
            autocomplete="off"
            placeholder="search here"
          />
        </b-form-group>
      </b-col>

      <b-col
        cols="6"
        md="2"
        class="px-1"
        order="1"
        order-md="2"
      >
        <b-form-group
          label="Partner Type"
          label-for="filter_partner_type"
          label-class="font-weight-bolder"
        >
          <b-select
            id="filter_partner_type"
            v-model="tableBusinessPartner.filter.partner_type"
            debounce="1000"
            :options="filterPartnerTypes"
            :disabled="state.busy || fetching.filterOptions"
          />
        </b-form-group>
      </b-col>

      <b-col
        cols="6"
        md="2"
        class="px-1"
        order="1"
        order-md="2"
      >
        <b-form-group
          label="Tax Status"
          label-for="filter_tax_status"
          label-class="font-weight-bolder"
        >
          <b-select
            id="filter_tax_status"
            v-model="tableBusinessPartner.filter.tax_status"
            debounce="1000"
            :options="filterTaxStatuses"
            :disabled="state.busy || fetching.filterOptions"
          />
        </b-form-group>
      </b-col>

      <b-col
        cols="12"
        order="3"
        class="px-1 mb-2"
      >
        <b-button
          size="sm"
          type="button"
          variant="success"
          @click="onCreateBusinessPartner"
        >
          Add Business Partner
        </b-button>
      </b-col>

    </b-row>
    <b-row class="mt-1">
      <b-col
        cols="12"
        class="px-1"
      >
        <b-table
          ref="tableBusinessPartner"
          hover
          small
          striped
          bordered
          responsive
          show-empty
          :stacked="isMobile()"
          :per-page="tableBusinessPartner.perPage"
          :current-page="tableBusinessPartner.currentPage"
          :items="tableBusinessPartnerProvider"
          :fields="tableBusinessPartner.fields"
          :sort-by.sync="tableBusinessPartner.sortBy"
          :sort-desc.sync="tableBusinessPartner.sortDesc"
          :sort-direction="tableBusinessPartner.sortDirection"
          :filter="tableBusinessPartner.filter"
          :filter-included-fields="tableBusinessPartner.filterOn"
          :busy="tableBusinessPartner.busy"
        >
          <template #cell(index)="row">
            {{ tableBusinessPartner.currentPage * tableBusinessPartner.perPage - tableBusinessPartner.perPage + (row.index + 1) }}
          </template>

          <template #cell(active)="row">
            {{ Number(row.value) === 1 ? 'Yes' : 'No' }}
          </template>

          <template #cell()="row">
            <div class="text-md-nowrap">
              {{ row.value }}
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="text-md-nowrap gap-2 d-flex flex-column flex-md-row justify-content-md-start justify-content-center">
              <b-button
                size="sm"
                variant="success"
                @click="onEditBusinessPartner(item)"
              >
                Edit
              </b-button>

              <b-button
                size="sm"
                @click.prevent="$refs.bankAccounts.showBankAccounts(item)"
              >
                Bank Accounts
              </b-button>

              <b-button
                size="sm"
                variant="info"
                @click.prevent="$refs.manual2307.createManual2307(item)"
              >
                Manual 2307
              </b-button>
            </div>
          </template>

          <template #cell(partner_name)="{ item, value }">
            <div
              :id="`popover-${item.id}`"
              class="d-flex flex-nowrap min-w-md-400px"
              style="cursor: help"
            >
              <span>{{ value }}</span>
            </div>
            <b-popover
              :target="`popover-${item.id}`"
              triggers="hover"
              placement="right"
              custom-class="bg-white min-w-md-600px w-md-600px max-w-1000px"
            >
              <template #title>
                <span class="small">{{ item.partner_name }}</span>
              </template>

              <b-row class="small gap-y-1">

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Active</b>
                  <div>{{ item.active === 1 ? 'Yes' : 'No' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="8"
                  class="mb-1"
                >
                  <b class="text-muted">TIN</b>
                  <div>{{ item.tin || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Vendor Type</b>
                  <div>{{ item.vendor_type || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Partner Type</b>
                  <div>{{ item.partner_type || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Customer Type</b>
                  <div>{{ item.customer_type || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Partner Code</b>
                  <div>{{ item.partner_code || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="8"
                  class="mb-1"
                >
                  <b class="text-muted">Partner Name</b>
                  <div>{{ item.partner_name || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="8"
                  offset-md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Trade Name</b>
                  <div>{{ item.trade_name || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Business Type</b>
                  <div>{{ item.business_type || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="8"
                  class="mb-1"
                >
                  <b class="text-muted">{{ item.business_type }} Name</b>
                  <div>{{ item.corporation_name || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Owner First Name</b>
                  <div>{{ item.owner_first_name || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Owner Last Name</b>
                  <div>{{ item.owner_last_name || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Owner Middle Name</b>
                  <div>{{ item.owner_middle_name || '---' }}</div>
                </b-col>
                <!-- <b-col
                  cols="4"
                  class="mb-1"
                >
                  <b class="text-muted">Payment Nature</b>
                  <div>{{ item.payment_nature_id ?? '---' }}</div>
                </b-col> -->

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Mobile Number</b>
                  <div>{{ item.mobile_number || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Telephone Number</b>
                  <div>{{ item.telephone_number || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Contact Person</b>
                  <div>{{ item.contact_person || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  class="mb-1"
                >
                  <b class="text-muted">Email</b>
                  <div>{{ item.email || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="8"
                  class="mb-1"
                >
                  <b class="text-muted">Address</b>
                  <div>{{ item.address || '---' }}</div>
                </b-col>

                <b-col
                  cols="4"
                  class="mb-1"
                >
                  <b class="text-muted">Zip Code</b>
                  <div>{{ item.zip_code || '---' }}</div>
                </b-col>

                <b-col
                  cols="4"
                  class="mb-1"
                >
                  <b class="text-muted">Business Tax</b>
                  <div>{{ item.business_tax || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Tax Code</b>
                  <div>
                    {{ item.tax_code ? `${item.tax_code.code}` : '---' }}
                  </div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Tax Description</b>
                  <div>
                    {{ item.tax_code ? `${item.tax_code.description}` : '---' }}
                  </div>
                </b-col>

                <b-col
                  cols="4"
                  class="mb-1"
                >
                  <b class="text-muted">Tax Status</b>
                  <div>{{ item.tax_status || '---' }}</div>
                </b-col>

                <b-col
                  cols="12"
                  md="4"
                  class="mb-1"
                >
                  <b class="text-muted">Tax Percentage</b>
                  <div>
                    {{ item.tax_code ? `${item.tax_code.percentage}` : '---' }}
                  </div>
                </b-col>

                <b-col
                  cols="4"
                  class="mb-1"
                >
                  <b class="text-muted">Document Type</b>
                  <div>
                    {{ item.document_type ? item.document_type.document_type_name : '---' }}
                  </div>
                </b-col>

                <b-col
                  cols="4"
                  class="mb-1"
                >
                  <b class="text-muted">Transaction Type</b>
                  <div>{{ item.transaction_type || '---' }}</div>
                </b-col>

                <b-col
                  cols="4"
                  class="mb-1"
                >
                  <b class="text-muted">Created At</b>
                  <div>{{ item.created_at ? dateTimeShortFormatter(item.created_at) : '---' }}</div>
                </b-col>

                <b-col
                  cols="4"
                  class="mb-1"
                >
                  <b class="text-muted">Updated At</b>
                  <div>{{ item.updated_at ? dateTimeShortFormatter(item.updated_at) : '---' }}</div>
                </b-col>
              </b-row>
            </b-popover>
          </template>

          <template #table-busy>
            <div class="text-center py-5">
              <b-icon
                icon="stopwatch"
                font-scale="5"
                animation="cylon"
              />
              <p class="h3 py-2">
                <strong>Loading . . .</strong>
              </p>
            </div>
          </template>

        </b-table>
      </b-col>
      <b-col
        cols="12"
        md="6"
        class="mt-1 mb-2 px-1"
      >
        <b-select
          v-model="tableBusinessPartner.perPage"
          :options="tableBusinessPartner.pageOptions"
          class="w-100 w-md-25"
        />
      </b-col>
      <b-col
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end mt-1 px-1"
      >
        <b-pagination
          v-model="tableBusinessPartner.currentPage"
          pills
          last-number
          first-number
          prev-text="Prev"
          next-text="Next"
          aria-controls="table"
          :total-rows="tableBusinessPartner.totalRows"
          :per-page="tableBusinessPartner.perPage"
        />
      </b-col>
    </b-row>

    <b-modal
      ref="modalBusinessPartner"
      size="xl"
      scrollable
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      hide-header-close
      modal-class="fullscreen"
      :title="ModalBusinessPartnerTitle"
    >
      <b-container
        class="h-100 py-1"
        style="overflow-y: scroll"
        fluid
      >
        <ValidationObserver
          ref="formBusinessPartner"
        >
          <form
            role="form"
            novalidate
            @submit.prevent
          >
            <b-row>
              <b-col
                cols="12"
                md="4"
              >
                <b-row>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="partner code"
                      vid="partner_code"
                      rules="max:30"
                    >
                      <b-form-group
                        label="Partner Code"
                        label-for="partner_code"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-input
                          id="partner_code"
                          v-model="businessPartner.partner_code"
                          type="text"
                          maxlength="30"
                          autocomplete="off"
                          placeholder="enter partner code here"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy"
                        />
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="partner name"
                      vid="partner_name"
                      rules="required|max:255"
                    >
                      <b-form-group
                        label="Partner Name"
                        label-for="partner_name"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-textarea
                          id="partner_name"
                          v-model="businessPartner.partner_name"
                          rows="3"
                          max-rows="6"
                          maxlength="255"
                          autocomplete="off"
                          placeholder="enter partner name here"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy"
                          @keydown="onPreventEnterOnKeyboard"
                        />
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="tin"
                      vid="tin"
                      rules="required|min:18|max:18"
                    >
                      <b-form-group
                        label="TIN"
                        label-for="partner_tin"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-input
                          id="partner_tin"
                          v-model="businessPartner.tin"
                          v-mask="'###-###-###-###NNN'"
                          type="text"
                          maxlength="18"
                          autocomplete="off"
                          placeholder="enter tin here"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy"
                        />
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="partner type"
                      vid="partner_type"
                      rules="required"
                    >
                      <b-form-group
                        label="Partner Type"
                        label-for="partner_type"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-form-select
                          id="partner_type"
                          v-model="businessPartner.partner_type"
                          :options="lists.partnerTypes"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy || fetching.options"
                        >
                          <template #first>
                            <b-form-select-option
                              :value="null"
                              disabled
                            >
                              -- select partner type here --
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    v-if="businessPartner.partner_type == 'Customer'"
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="customer type"
                      vid="customer_type"
                      rules="required_if:partner_type,Customer"
                    >
                      <b-form-group
                        label="Customer Type"
                        label-for="customer_type"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-form-select
                          id="customer_type"
                          v-model="businessPartner.customer_type"
                          :options="lists.customerTypes"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy || fetching.options"
                        >
                          <template #first>
                            <b-form-select-option
                              :value="null"
                              disabled
                            >
                              -- select customer type here --
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    v-else
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="vendor type"
                      vid="vendor_type"
                      rules="required_if:partner_type,Vendor"
                    >
                      <b-form-group
                        label="Vendor Type"
                        label-for="vendor_type"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-form-select
                          id="vendor_type"
                          v-model="businessPartner.vendor_type"
                          :options="lists.vendorTypes"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy || fetching.options"
                        >
                          <template #first>
                            <b-form-select-option
                              :value="null"
                              disabled
                            >
                              -- select vendor type here --
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <b-row>

                      <b-col
                        cols="12"
                      >
                        <ValidationProvider
                          #default="{ errors }"
                          name="business type"
                          vid="business_type"
                          rules="required_if:partner_type,Vendor"
                        >
                          <b-form-group
                            label="Business Type"
                            label-for="business_type"
                            label-cols-md="4"
                            label-class="font-weight-bolder"
                            class="mb-2"
                          >
                            <b-form-select
                              id="business_type"
                              v-model="businessPartner.business_type"
                              :options="lists.businessTypes"
                              :state="errors.length > 0 ? false : null"
                              :disabled="state.busy || fetching.options || businessPartner.partner_type !== 'Vendor'"
                            >
                              <template #first>
                                <b-form-select-option
                                  :value="null"
                                  disabled
                                >
                                  -- select business type here --
                                </b-form-select-option>
                              </template>
                            </b-form-select>
                            <div
                              v-if="errors.length > 0"
                              class="invalid-feedback"
                            >
                              <span>{{ errors[0] }}</span>
                            </div>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>

                      <b-col
                        cols="12"
                      >
                        <ValidationProvider
                          #default="{ errors }"
                          name="trade name"
                          vid="trade_name"
                          rules="required_if:partner_type,Vendor|max:255"
                        >
                          <b-form-group
                            label="Trade Name"
                            label-for="trade_name"
                            label-class="font-weight-bolder"
                            class="mb-2"
                          >
                            <b-textarea
                              id="trade_name"
                              v-model="businessPartner.trade_name"
                              rows="3"
                              max-rows="6"
                              maxlength="255"
                              autocomplete="off"
                              placeholder="enter trade name here"
                              :state="errors.length > 0 ? false : null"
                              :disabled="state.busy || businessPartner.partner_type !== 'Vendor'"
                              @keydown="onPreventEnterOnKeyboard"
                            />
                            <div
                              v-if="errors.length > 0"
                              class="invalid-feedback"
                            >
                              <span>{{ errors[0] }}</span>
                            </div>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>

                      <b-col
                        cols="12"
                      >
                        <ValidationProvider
                          #default="{ errors }"
                          name="corporation name"
                          vid="corporation_name"
                          rules="required_if:business_type,Corporation,Cooperative,Government|max:255"
                        >
                          <b-form-group
                            :label="(`${businessPartner.business_type || 'Business Type'} Name`)"
                            label-for="corporation_name"
                            label-class="font-weight-bolder"
                            class="mb-2"
                          >
                            <b-textarea
                              id="corporation_name"
                              v-model="businessPartner.corporation_name"
                              rows="3"
                              max-rows="6"
                              maxlength="255"
                              autocomplete="off"
                              :placeholder="(`enter ${businessPartner.business_type?.toLowerCase() || 'business type'} name here`)"
                              :state="errors.length > 0 ? false : null"
                              :disabled="state.busy || businessPartner.partner_type !== 'Vendor' || businessPartner.business_type === null"
                              @keydown="onPreventEnterOnKeyboard"
                            />
                            <div
                              v-if="errors.length > 0"
                              class="invalid-feedback"
                            >
                              <span>{{ errors[0] }}</span>
                            </div>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>

                    </b-row>
                  </b-col>

                </b-row>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-row>

                  <b-col
                    cols="12"
                  >
                    <b-row>
                      <b-col
                        cols="12"
                      >
                        <b-form-group
                          label="Owner / Authorized Representative"
                          label-cols-md="12"
                          label-class="font-weight-bolder"
                          class="mb-2"
                        />
                      </b-col>
                      <b-col
                        cols="12"
                      >
                        <ValidationProvider
                          #default="{ errors }"
                          name="first name"
                          vid="owner_first_name"
                          rules="required_if:business_type,Sole-Proprietorship|max:50"
                        >
                          <b-form-group
                            label="First Name"
                            label-for="owner_first_name"
                            label-cols-md="4"
                            label-class="font-weight-bolder"
                            class="mb-2"
                          >
                            <b-input
                              id="owner_first_name"
                              v-model="businessPartner.owner_first_name"
                              type="text"
                              maxlength="50"
                              autocomplete="off"
                              placeholder="enter first name here"
                              :state="errors.length > 0 ? false : null"
                              :disabled="state.busy || businessPartner.partner_type !== 'Vendor' || businessPartner.business_type !== 'Sole-Proprietorship'"
                            />
                            <div
                              v-if="errors.length > 0"
                              class="invalid-feedback"
                            >
                              <span>{{ errors[0] }}</span>
                            </div>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>

                      <b-col
                        cols="12"
                      >
                        <ValidationProvider
                          #default="{ errors }"
                          name="contact person"
                          vid="owner_middle_name"
                          rules="max:100"
                        >
                          <b-form-group
                            label="Middle Name"
                            label-for="owner_middle_name"
                            label-cols-md="4"
                            label-class="font-weight-bolder"
                            class="mb-2"
                          >
                            <b-input
                              id="owner_middle_name"
                              v-model="businessPartner.owner_middle_name"
                              type="text"
                              maxlength="100"
                              autocomplete="off"
                              placeholder="enter middle name here"
                              :state="errors.length > 0 ? false : null"
                              :disabled="state.busy || businessPartner.partner_type !== 'Vendor' || businessPartner.business_type !== 'Sole-Proprietorship'"
                            />
                            <div
                              v-if="errors.length > 0"
                              class="invalid-feedback"
                            >
                              <span>{{ errors[0] }}</span>
                            </div>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>

                      <b-col
                        cols="12"
                      >
                        <ValidationProvider
                          #default="{ errors }"
                          name="surname"
                          vid="owner_last_name"
                          rules="required_if:business_type,Sole-Proprietorship|max:100"
                        >
                          <b-form-group
                            label="Surname"
                            label-for="owner_last_name"
                            label-cols-md="4"
                            label-class="font-weight-bolder"
                            class="mb-2"
                          >
                            <b-input
                              id="owner_last_name"
                              v-model="businessPartner.owner_last_name"
                              type="text"
                              maxlength="100"
                              autocomplete="off"
                              placeholder="enter surname here"
                              :state="errors.length > 0 ? false : null"
                              :disabled="state.busy || businessPartner.partner_type !== 'Vendor' || businessPartner.business_type !== 'Sole-Proprietorship'"
                            />
                            <div
                              v-if="errors.length > 0"
                              class="invalid-feedback"
                            >
                              <span>{{ errors[0] }}</span>
                            </div>
                          </b-form-group>
                        </ValidationProvider>
                      </b-col>

                    </b-row>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="telephone number"
                      vid="telephone_number"
                      rules="max:30"
                    >
                      <b-form-group
                        label="Tel. Number"
                        label-for="telephone_number"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-input
                          id="telephone_number"
                          v-model="businessPartner.telephone_number"
                          type="text"
                          maxlength="30"
                          autocomplete="off"
                          placeholder="enter telephone number here"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy"
                        />
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="mobile number"
                      vid="mobile_number"
                      rules="min:11|max:11"
                    >
                      <b-form-group
                        label="Mobile Number"
                        label-for="mobile_number"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-input
                          id="mobile_number"
                          v-model="businessPartner.mobile_number"
                          type="text"
                          maxlength="11"
                          autocomplete="off"
                          placeholder="enter mobile number here"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy"
                        />
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="email"
                      vid="email"
                      rules="email|max:50"
                    >
                      <b-form-group
                        label="Email"
                        label-for="email"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-input
                          id="email"
                          v-model="businessPartner.email"
                          type="text"
                          maxlength="50"
                          autocomplete="off"
                          placeholder="enter email here"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy"
                        />
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="contact person"
                      vid="contact_person"
                      rules="max:100"
                    >
                      <b-form-group
                        label="Contact Person"
                        label-for="contact_person"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-input
                          id="contact_person"
                          v-model="businessPartner.contact_person"
                          type="text"
                          maxlength="100"
                          autocomplete="off"
                          placeholder="enter contact person here"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy"
                        />
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="address"
                      vid="address"
                      rules="max:255"
                    >
                      <b-form-group
                        label="Address"
                        label-for="address"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-textarea
                          id="address"
                          v-model="businessPartner.address"
                          rows="3"
                          max-rows="6"
                          maxlength="255"
                          autocomplete="off"
                          placeholder="enter address here"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy"
                        />
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="zip code"
                      vid="zip_code"
                      rules="max:4"
                    >
                      <b-form-group
                        label="Zip Code"
                        label-for="zip_code"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-input
                          id="zip_code"
                          v-model="businessPartner.zip_code"
                          type="text"
                          maxlength="4"
                          autocomplete="off"
                          placeholder="enter zip code here"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy"
                        />
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                </b-row>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-row>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="tax status"
                      vid="tax_status"
                      rules="required"
                    >
                      <b-form-group
                        label="Tax Status"
                        label-for="tax_status"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-form-select
                          id="tax_status"
                          v-model="businessPartner.tax_status"
                          :options="lists.taxStatuses"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy || fetching.options"
                        >
                          <template #first>
                            <b-form-select-option
                              :value="null"
                              disabled
                            >
                              -- select tax status here --
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="business tax"
                      vid="business_tax"
                      rules="required"
                    >
                      <b-form-group
                        label="Business Tax"
                        label-for="business_tax"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-form-select
                          id="business_tax"
                          v-model="businessPartner.business_tax"
                          :options="lists.businessTaxes"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy || fetching.options"
                        >
                          <template #first>
                            <b-form-select-option
                              :value="null"
                              disabled
                            >
                              -- select business tax here --
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="tax code"
                      vid="tax_code"
                      rules=""
                    >
                      <b-form-group
                        label="Tax Code"
                        label-for="tax_code"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-form-select
                          id="tax_code"
                          v-model="businessPartner.tax_code"
                          :options="taxCodes"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy || fetching.options"
                        >
                          <template #first>
                            <b-form-select-option
                              :value="null"
                              disabled
                            >
                              -- select tax code here --
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="document type"
                      vid="document_type"
                      rules=""
                    >
                      <b-form-group
                        label="Document Type"
                        label-for="document_type"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-form-select
                          id="document_type"
                          v-model="businessPartner.document_type"
                          :options="documentTypes"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy || fetching.options"
                        >
                          <template #first>
                            <b-form-select-option
                              :value="null"
                              disabled
                            >
                              -- select document type here --
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="transaction type"
                      vid="transaction_type"
                      rules="required"
                    >
                      <b-form-group
                        label="Transaction Type"
                        label-for="transaction_type"
                        label-cols-md="4"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <b-form-select
                          id="transaction_type"
                          v-model="businessPartner.transaction_type"
                          :options="lists.transactionTypes"
                          :state="errors.length > 0 ? false : null"
                          :disabled="state.busy || fetching.options"
                        >
                          <template #first>
                            <b-form-select-option
                              :value="null"
                              disabled
                            >
                              -- select transaction type here --
                            </b-form-select-option>
                          </template>
                        </b-form-select>
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    cols="12"
                  >
                    <ValidationProvider
                      #default="{ errors }"
                      name="payment nature"
                      vid="payment_nature"
                    >
                      <b-form-group
                        label="Payment Nature"
                        label-for="payment_nature"
                        label-class="font-weight-bolder"
                        class="mb-2"
                      >
                        <v-select
                          v-model="selected.paymentNature"
                          input-id="payment_nature"
                          type="text"
                          append-to-body
                          label="nature_name"
                          placeholder="search payment nature here"
                          :options="lists.paymentNatures"
                          :calculate-position="calculateDropPosition"
                          :class="errors.length > 0 ? 'is-invalid' : null"
                          :disabled="state.busy || fetching.options"
                          :loading="fetching.options"
                        >
                          <template #no-options="">
                            no available payment nature
                          </template>
                        </v-select>
                        <div
                          v-if="errors.length > 0"
                          class="invalid-feedback"
                        >
                          <span>{{ errors[0] }}</span>
                        </div>
                      </b-form-group>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    v-if="state.editing"
                    cols="12"
                  >
                    <b-form-group>
                      <b-form-checkbox
                        id="is_active"
                        v-model="businessPartner.active"
                        switch
                        value="1"
                        name="is_active"
                        unchecked-value="0"
                        :disabled="state.busy"
                      >
                        Is Active?
                      </b-form-checkbox>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>

          </form>
        </ValidationObserver>

      </b-container>
      <template #modal-footer="{cancel}">
        <div class="col-12 d-flex justify-content-between px-0 pb-0 mb-0">
          <div
            v-if="state.editing"
          >
            <b-button
              variant="secondary"
              :disabled="state.busy"
              @click.prevent="$refs.bankAccounts.showBankAccounts(businessPartner)"
            >
              Bank Accounts
            </b-button>
          </div>
          <div />
          <div>
            <b-button
              class="mr-1"
              variant="success"
              :disabled="state.busy"
              @click.prevent="onValidateBusinessPartner"
            >
              {{ state.editing ? 'Update Record' : 'Save Record' }}
            </b-button>
            <b-button
              variant="outline-dark"
              :disabled="state.busy"
              @click.prevent="cancel()"
            >
              Close <span class="d-none d-md-inline-block">Window</span>
            </b-button>
          </div>
        </div>
      </template>

    </b-modal>

    <BankAccounts
      ref="bankAccounts"
    />

    <Manual2307
      ref="manual2307"
    />
  </b-container>
</template>

<script>
import QUERY from 'lodash'
import { core } from '@/config/pluginInit'

import BankAccounts from '@/views/ta/maintenances/business-partners/components/BankAccounts.vue'
import Manual2307 from '@/views/ta/maintenances/business-partners/components/Manual2307.vue'

import FORMATTER from '@/mixins/formatter'
import MISC from '@/mixins/misc'
import BIR2307 from '@/mixins/printing/bir-2307'
import MTaxCodes from '@/mixins/maintenances/tax-codes'

import { STABusinessPartner, SSharedList } from '@/services'

export default {
  name: 'TaMaintenancesBusinessPartners',

  middleware: ['auth', 'ta'],

  components: {
    BankAccounts,
    Manual2307
  },

  mixins: [
    FORMATTER,
    MISC,
    BIR2307,
    MTaxCodes
  ],

  metaInfo: () => ({
    title: 'Business Partners'
  }),

  props: {
    currentTabIndex: {
      type: Number,
      default: () => {
        return 1
      }
    }
  },

  data () {
    return {
      state: {
        busy: false,
        editing: false,
        bankEditing: false
      },
      fetching: {
        partnerTypes: false,
        taxStatuses: false,
        options: false,
        filterOptions: false,
        banks: false
      },
      lists: {
        banks: [],
        budgetCenters: [],
        businessTaxes: [],
        businessTypes: [
          // 'Corporation',
          // 'Sole-Proprietorship',
          // 'Government',
          // 'Cooperative'
        ],
        customerTypes: [],
        paymentNatures: [],
        documentTypes: [],
        fiscalYears: [],
        partnerTypes: [],
        taxCodes: [],
        taxStatuses: [],
        vendorTypes: [],
        transactionTypes: []
      },
      selected: {
        paymentNature: null,
        bank: null
      },
      businessPartner: {
        id: null,
        partner_code: null,
        partner_name: null,
        tin: null,
        partner_type: null,
        vendor_type: null,
        business_type: null,
        trade_name: null,
        corporation_name: null,
        owner_first_name: null,
        owner_middle_name: null,
        owner_last_name: null,
        customer_type: null,
        telephone_number: null,
        mobile_number: null,
        email: null,
        address: null,
        zip_code: null,
        contact_person: null,
        tax_status: null,
        business_tax: null,
        tax_code: null,
        document_type: null,
        transaction_type: null,
        payment_nature: null,
        bank_account_details: [],
        active: 1
      },
      tableBusinessPartner: {
        perPage: 10,
        pageOptions: [10, 25, 50, 100],
        totalRows: 0,
        currentPage: 1,
        sortBy: null,
        sortDesc: false,
        sortDirection: 'asc',
        filter: {
          search: null,
          partner_type: 'All',
          tax_status: 'All'
        },
        filterOn: [],
        busy: false,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 13, key: 'actions', class: 'text-center' },
          { mobile: 1, key: 'created_at', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'partner_code' },
          { mobile: 3, key: 'partner_name' },
          { mobile: 4, key: 'tin' },
          { mobile: 5, key: 'partner_type' },
          { mobile: 6, key: 'vendor_type' },
          { mobile: 7, key: 'customer_type' },
          { mobile: 8, key: 'telephone_number', label: 'Telephone' },
          { mobile: 9, key: 'mobile_number', label: 'Mobile' },
          { mobile: 10, key: 'email' },
          { mobile: 11, key: 'active', class: 'text-center' },
          { mobile: 12, key: 'updated_at', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ModalBusinessPartnerTitle () {
      return this.state.editing ? 'Edit Business Partner' : 'Add Business Partner'
    },

    filterPartnerTypes () {
      return [{ text: 'All', value: 'All' }].concat(this.lists.partnerTypes)
    },

    filterTaxStatuses () {
      return [{ text: 'All', value: 'All' }].concat(this.lists.taxStatuses)
    },

    documentTypes () {
      return [{ text: 'NONE', value: null }].concat(this.lists.documentTypes)
    },

    taxCodes () {
      return [{ text: 'NONE', value: null }].concat(this.lists.taxCodes)
    },

    paymentNatures () {
      return [{ text: 'NONE', value: null }].concat(this.lists.paymentNatures)
    }
  },

  watch: {

    'businessPartner.partner_name' (partnerName) {
      if (partnerName?.search(/\n/g)) {
        this.businessPartner.partner_name = partnerName.replace(/\n/g, '')
      }
    },

    'businessPartner.partner_type' (partnerType) {
      if (partnerType) {
        if (partnerType === 'Vendor') {
          this.businessPartner.customer_type = null
        }

        if (partnerType === 'Customer') {
          this.businessPartner = Object.assign(this.businessPartner, {
            business_type: null,
            trade_name: null,
            corporation_name: null,
            owner_first_name: null,
            owner_middle_name: null,
            owner_last_name: null,
            vendor_type: null
          })
        }
      } else {
        this.businessPartner = Object.assign(this.businessPartner, {
          business_type: null,
          customer_type: null,
          vendor_type: null,
          trade_name: null,
          corporation_name: null,
          owner_first_name: null,
          owner_middle_name: null,
          owner_last_name: null
        })
      }
    },

    'selected.paymentNature' (paymentNature) {
      this.businessPartner.payment_nature = paymentNature?.id || null
    },

    'selected.taxCode' (taxCode) {
      this.businessPartner.tax_code = taxCode?.id || null
    }
  },

  mounted () {
    core.index()
    this.getFilterOptions()
  },

  methods: {

    onPreventEnterOnKeyboard (event) {
      if (event.keyCode === 13) {
        event.preventDefault()
      }
    },

    // BUSINESS PARTNER
    async tableBusinessPartnerProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableBusinessPartner.busy = true
      const payload = this.objectToUrl({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        partner_type: filter.partner_type,
        tax_status: filter.tax_status
      })

      return await STABusinessPartner.get(payload).then(({ data }) => {
        this.tableBusinessPartner.totalRows = data.total_rows
        return data.items
      }).catch(() => {
        this.tableBusinessPartner.totalRows = 0
        return []
      }).finally(() => {
        this.tableBusinessPartner.busy = false
      })
    },

    async getFilterOptions () {
      this.fetching.filterOptions = true

      const filters = this.toArrayListParam([
        'partner_types',
        'tax_statuses'
      ])

      return new Promise(resolve => {
        SSharedList.getCustomList(filters).then(({ data }) => {
          this.lists = Object.assign(this.lists, {
            partnerTypes: data.partner_types,
            taxStatuses: data.tax_statuses
          })
          resolve(data)
        })
      }).catch(() => {
        this.lists = Object.assign(this.lists, {
          partnerTypes: [],
          taxStatuses: []
        })
      }).finally(() => {
        this.fetching.filterOptions = false
      })
    },

    async getOptions () {
      this.fetching.options = true

      const filters = this.toArrayListParam([
        'business_taxes',
        'business_types',
        'customer_types',
        'document_types',
        'payment_natures',
        'tax_codes',
        'vendor_types',
        'transaction_types'
      ])

      return new Promise(resolve => {
        SSharedList.getCustomList(filters).then(({ data }) => {
          this.lists = Object.assign(this.lists, {
            businessTypes: data.business_types,
            vendorTypes: data.vendor_types,
            customerTypes: data.customer_types,
            paymentNatures: data.payment_natures,
            businessTaxes: data.business_taxes,
            transactionTypes: data.transaction_types,
            taxCodes: data.tax_codes.map(
              taxCode => ({
                text: `${taxCode.code} - ${taxCode.description}`,
                value: taxCode.id
              })
            ),
            documentTypes: data.document_types.map(
              documentType => ({
                text: documentType.document_type_name,
                value: documentType.id
              })
            )
          })
          resolve(data)
        })
      }).catch(() => {
        this.lists = Object.assign(this.lists, {
          businessTypes: [],
          vendorTypes: [],
          customerTypes: [],
          paymentNatures: [],
          businessTaxes: [],
          transactionTypes: [],
          taxCodes: [],
          documentTypes: []
        })
      }).finally(() => {
        this.fetching.options = false
      })
    },

    onCreateBusinessPartner () {
      this.state.editing = false

      this.businessPartner = {
        id: null,
        partner_code: null,
        partner_name: null,
        tin: null,
        partner_type: null,
        vendor_type: null,
        business_type: null,
        trade_name: null,
        corporation_name: null,
        owner_first_name: null,
        owner_middle_name: null,
        owner_last_name: null,
        customer_type: null,
        telephone_number: null,
        mobile_number: null,
        email: null,
        address: null,
        zip_code: null,
        contact_person: null,
        tax_status: null,
        business_tax: null,
        tax_code: null,
        document_type: null,
        transaction_type: null,
        payment_nature: null,
        active: 1
      }

      this.getOptions()

      this.$refs.modalBusinessPartner.show()
    },

    onEditBusinessPartner (businessPartner) {
      this.state.editing = true

      this.businessPartner = {
        id: businessPartner.id,
        partner_code: businessPartner.partner_code,
        partner_name: businessPartner.partner_name,
        tin: businessPartner.tin,
        partner_type: businessPartner.partner_type,
        vendor_type: businessPartner.vendor_type,
        business_type: businessPartner.business_type,
        trade_name: businessPartner.trade_name,
        corporation_name: businessPartner.corporation_name,
        owner_first_name: businessPartner.owner_first_name,
        owner_middle_name: businessPartner.owner_middle_name,
        owner_last_name: businessPartner.owner_last_name,
        customer_type: businessPartner.customer_type,
        telephone_number: businessPartner.telephone_number,
        mobile_number: businessPartner.mobile_number,
        email: businessPartner.email,
        address: businessPartner.address,
        zip_code: businessPartner?.zip_code || null,
        contact_person: businessPartner.contact_person,
        tax_status: businessPartner.tax_status,
        business_tax: businessPartner.business_tax,
        tax_code: businessPartner.tax_code_id,
        document_type: businessPartner.document_type_id,
        transaction_type: businessPartner.transaction_type,
        active: businessPartner.active
      }

      this.getOptions().then(() => {
        this.selected.paymentNature = QUERY.find(this.lists.paymentNatures, {
          id: businessPartner.payment_nature_id
        })
      })

      this.$refs.modalBusinessPartner.show()
    },

    async onValidateBusinessPartner () {
      await this.$refs.formBusinessPartner.validate().then(
        async validBusinessPartner => {
          if (!validBusinessPartner) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: () => {
              if (this.state.editing) {
                return '<h6>Update Business Partner?</h6>'
              }
              return '<h6>Create Business Partner?</h6>'
            },
            preConfirm: () => {
              this.state.busy = true

              const createOrUpdate = () => {
                return this.state.editing ? this.onPutBusinessPartner() : this.onPostBusinessPartner()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  this.$refs.formBusinessPartner.setErrors(message)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async onPostBusinessPartner () {
      return STABusinessPartner.post(this.businessPartner).then(
        ({ data }) => {
          this.$refs.tableBusinessPartner.refresh()
          this.swalSuccess(data.message).then(() => {
            this.$bvModal.hide('modal-business-partner')
          })
          return data
        }
      )
    },

    async onPutBusinessPartner () {
      return STABusinessPartner.put(this.businessPartner).then(
        ({ data }) => {
          const tableBusinessPartnerRow = QUERY.find(this.$refs.tableBusinessPartner.localItems, {
            id: data.business_partner.id
          })
          if (tableBusinessPartnerRow) {
            tableBusinessPartnerRow.partner_code = data.business_partner.partner_code
            tableBusinessPartnerRow.partner_name = data.business_partner.partner_name
            tableBusinessPartnerRow.tin = data.business_partner.tin
            tableBusinessPartnerRow.partner_type = data.business_partner.partner_type
            tableBusinessPartnerRow.vendor_type = data.business_partner.vendor_type
            tableBusinessPartnerRow.customer_type = data.business_partner.customer_type
            tableBusinessPartnerRow.business_type = data.business_partner.business_type
            tableBusinessPartnerRow.trade_name = data.business_partner.trade_name
            tableBusinessPartnerRow.corporation_name = data.business_partner.corporation_name
            tableBusinessPartnerRow.owner_first_name = data.business_partner.owner_first_name
            tableBusinessPartnerRow.owner_middle_name = data.business_partner.owner_middle_name
            tableBusinessPartnerRow.owner_last_name = data.business_partner.owner_last_name
            tableBusinessPartnerRow.telephone_number = data.business_partner.telephone_number
            tableBusinessPartnerRow.mobile_number = data.business_partner.mobile_number
            tableBusinessPartnerRow.email = data.business_partner.email
            tableBusinessPartnerRow.contact_person = data.business_partner.contact_person
            tableBusinessPartnerRow.address = data.business_partner.address
            tableBusinessPartnerRow.tax_status = data.business_partner.tax_status
            tableBusinessPartnerRow.business_tax = data.business_partner.business_tax
            tableBusinessPartnerRow.tax_code_id = data.business_partner.tax_code_id
            tableBusinessPartnerRow.tax_code = data.business_partner.tax_code
            tableBusinessPartnerRow.document_type_id = data.business_partner.document_type_id
            tableBusinessPartnerRow.document_type = data.business_partner.document_type
            tableBusinessPartnerRow.payment_nature_id = data.business_partner.payment_nature_id
            tableBusinessPartnerRow.active = data.business_partner.active
            tableBusinessPartnerRow.updated_at = data.business_partner.updated_at
          }

          this.swalSuccess(data.message).then(() => {
            this.$bvModal.hide('modal-business-partner')
          })

          return data
        }
      )
    }
  }
}
</script>
